// src/modules/vehiculos/components/MiniMapaVehiculo.jsx
import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import L from "leaflet";

const customIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  shadowSize: [41, 41],
});

// Los tiles de CARTO (basemaps.cartocdn.com) dejaron de servir sin API key —
// devuelven una imagen con marca de agua "API KEY REQUIRED" en vez del mapa.
// Se reemplazan por los tiles estandar de OpenStreetMap (gratis, sin key). No
// tienen una variante oscura propia, asi que "Oscuro" reusa el mismo tile con
// un filtro CSS (invertir colores) via la clase "map-tiles-dark" mas abajo.
const tileLayers = {
  light: {
    name: "Claro",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  dark: {
    name: "Oscuro",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    className: "map-tiles-dark",
  },
  satellite: {
    name: "Satélite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles © Esri",
  },
};

// Recentrado estable: sin animación y evitando repeticiones innecesarias
function StableCenter({ lat, lon }) {
  const map = useMap();
  const last = useRef({ lat: null, lon: null });
  useEffect(() => {
    if (lat == null || lon == null) return;

    // Compara contra el centro actual con tolerancia para evitar “temblores”
    const c = map.getCenter();
    const EPS = 1e-6;
    const sameAsCurrent =
      Math.abs(c.lat - lat) < EPS && Math.abs(c.lng - lon) < EPS;

    const sameAsLast =
      last.current.lat === lat && last.current.lon === lon;

    if (!sameAsCurrent && !sameAsLast) {
      // Sin animación
      map.setView([lat, lon], map.getZoom(), { animate: false });
      last.current = { lat, lon };
      // Asegura layout correcto tras reflow del panel sticky
      requestAnimationFrame(() => {
        map.invalidateSize();
      });
    }
  }, [lat, lon, map]);

  return null;
}

export default function MiniMapaVehiculo({
  latitud,
  longitud,
  alias,
  velocidad,
  initialTipoMapa = "light",
}) {
  const [tipoMapa, setTipoMapa] = useState(initialTipoMapa);
  const [anchorEl, setAnchorEl] = useState(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
    } else {
      setTipoMapa(initialTipoMapa);
    }
  }, [initialTipoMapa]);

  const hasCoords = latitud != null && longitud != null;
  const position = [latitud ?? 0, longitud ?? 0];

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleChangeMapa = (tipo) => {
    setTipoMapa(tipo);
    handleCloseMenu();
  };

  return (
    <div style={{ position: "relative", marginTop: 10 }}>
      {/* OSM no tiene un estilo oscuro propio gratuito — se simula invirtiendo
          los colores del mismo tile claro via CSS. hue-rotate compensa el
          invert() para que el agua/vegetacion no queden con tinte naranja. */}
      <style>{`
        .map-tiles-dark { filter: invert(1) hue-rotate(180deg) brightness(0.95) contrast(0.9); }
      `}</style>
      {/* Switch de tipo de mapa */}
      <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
        <Tooltip title="Cambiar tipo de mapa">
          <IconButton color="primary" onClick={handleOpenMenu}>
            <MapIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          <MenuItem onClick={() => handleChangeMapa("light")}>Claro</MenuItem>
          <MenuItem onClick={() => handleChangeMapa("dark")}>Oscuro</MenuItem>
          <MenuItem onClick={() => handleChangeMapa("satellite")}>Satélite</MenuItem>
        </Menu>
      </div>

      {!hasCoords ? (
        <div
          style={{
            height: 360,
            width: "100%",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.04)",
          }}
        >
          No hay datos de ubicación.
        </div>
      ) : (
        <MapContainer
          center={position}           // solo inicial
          zoom={15}
          style={{ height: 360, width: "100%", borderRadius: 12 }}
          scrollWheelZoom={false}
          preferCanvas={true}
        >
          <TileLayer
            url={tileLayers[tipoMapa].url}
            attribution={tileLayers[tipoMapa].attribution}
            className={tileLayers[tipoMapa].className}
          />
          {/* Re-centra sin animación y evita repeticiones */}
          <StableCenter lat={latitud} lon={longitud} />
          <Marker position={position} icon={customIcon}>
            <Popup>
              Vehículo: <strong>{alias ?? "Desconocido"}</strong>
              <br />
              Velocidad: {velocidad != null ? `${velocidad} km/h` : "No disponible"}
              <br />
              Lat: {latitud} · Lon: {longitud}
            </Popup>
          </Marker>
        </MapContainer>
      )}
    </div>
  );
}
